'use client'
import { type FC, memo, RefObject, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useParams, useSearchParams } from 'next/navigation'
import type { TypeCmsMedium, TypeCmsProductDetail, TypeLang } from '@/schema'
import { designGrid, mq, vw } from '@/styles'
import { NAVBAR_HEIGHTS } from './commons/layout/Navbar'
import { SectionAnchors } from './SectionAnchors'
import { SectionDescription } from './SectionDescription'
import { SectionSpecifications } from './SectionSpecifications'
import { SectionConfigurator } from './SectionConfigurator'
import { SectionContents } from './SectionContents'
import { SectionDownloadsPerModel } from './SectionDownloadsPerModel'
import { SectionExploreProjects } from './SectionExploreProjects'
import { SectionFitsIntoYourProject } from './SectionFitsIntoYourProject'
import { SectionRelateds } from './SectionRelateds'
import { SectionAccessories } from './SectionAccessories'
import dynamic from 'next/dynamic'
import { Breadcrumbs } from './Breadcrumbs'
import { AnimatePresence, motion } from 'framer-motion'
import { useModal, usePageTransition } from '@/hooks'
import { MODALS } from '@/context'

const DynamicModalZoom = dynamic(() => import('@/components/modals/ModalZoom').then(mod => mod.ModalZoom))
const DynamicModalSpecPdf = dynamic(() => import('@/components/modals/ModalPdfSpecSheet').then(mod => mod.ModalPdfSpecSheet))
const DynamicImageSliderVertical = dynamic(() => import('@/components/ImageSliderVertical').then(mod => mod.ImageSliderVertical), {
  ssr: false,
  loading: () => <div style={{ height: 'auto', width: '100%' }} /> // or skeleton placeholder
})

const DynamicProductSlider = dynamic(() => import('@/components/ProductSlider').then(mod => mod.ProductSlider), {
  ssr: false,
  loading: () => <div style={{ height: '160px', width: '100%' }} /> // or skeleton placeholder
})

type Props = {
  data: TypeCmsProductDetail
}

export type ModelWithColors = {
  id: number
  name: string
  color: number
  slug: string,
  medium? : TypeCmsMedium
}

const TRANSLATIONS_SECTIONS = {
  en: {
    accessories: 'Accessories',
    overview: 'Overview',
    specifications: 'Specifications',
    configurator: 'Configurator',
    contents: 'Contents',
    downloads: 'Downloads',
    projects: 'Projects',
    relateds: 'Related products',
  },
  es: {
    accessories: 'Accesorios',
    overview: 'Descripción',
    specifications: 'Especificaciones',
    configurator: 'Configurador',
    contents: 'Contenidos',
    downloads: 'Descargas',
    projects: 'Proyectos',
    relateds: 'Productos relacionados',
  },
  fr: {
    accessories: 'Accessoires',
    overview: 'Description',
    specifications: 'Spécifications',
    configurator: 'Configurateur',
    contents: 'Contenus',
    downloads: 'Téléchargements',
    projects: 'Projets',
    relateds: 'Produits connexes',
  },
  it: {
    accessories: 'Accessori',
    overview: 'Descrizione',
    specifications: 'Specifiche',
    configurator: 'Configuratore',
    contents: 'Contenuti',
    downloads: 'Scarica',
    projects: 'Progetti',
    relateds: 'Prodotti correlati',
  }
}

const getObj = (lang: TypeLang, keyname, ref) => {
  return {
    id: `#${keyname}`,
    title: TRANSLATIONS_SECTIONS[lang][keyname],
    ref
  }
}

const getPageAnchors = (data: TypeCmsProductDetail, lang: TypeLang, refs: { [key: string]: RefObject<HTMLElement> }) => {
  const anchors = []
  if (data.description) anchors.push(getObj(lang, 'overview', refs.refDescription))
  if (data.specifications?.length > 0) anchors.push(getObj(lang, 'specifications', refs.refSpecifications))
  if (data.configurator) anchors.push(getObj(lang, 'configurator', refs.refConfigurator))
  if (data.accessories?.length > 0) anchors.push(getObj(lang, 'accessories', refs.refAccessories))
  if (data.models?.length > 0) anchors.push(getObj(lang, 'downloads', refs.refDownloads))
  if (data.contents?.length > 0) anchors.push(getObj(lang, 'contents', refs.refContents))
  if (data.projects?.length > 0) anchors.push(getObj(lang, 'projects', refs.refProjects))
  if (data.related_products?.length > 0) anchors.push(getObj(lang, 'relateds', refs.refRelateds))
  return anchors
}

let urlChecked = false
export const MainProduct:FC<Props> = memo(({ data }) => {
  const { accessories, description, configurator, contents, filter, models, name, projects, related_products, specifications, media, category, display_available_colors:displayAvailableColorsTitle } = data

  const { lang, slug }: { lang: TypeLang, slug: string } = useParams()
  const searchParams = useSearchParams()
  const searchParamSpecSheet = searchParams.get('specsheet')
  const { updatePayload:updatePayloadModalSpecPdf } = useModal(MODALS.PDP_SPEC_PDF)
  const { setTransitionName, setCortinaTransitionName, setIsLoading } = usePageTransition()

  const [selectedModel, setSelectedModel] = useState(models[0]?.slug ? `${models[0]?.id}`+'_'+`${models[0]?.slug}` : null)
  const [colorsOfSelectedModel, setColorsOfSelectedModel] = useState([])
  const [colorSelected, setColorSelected] = useState(colorsOfSelectedModel[0]?.ids?.join(',') ?? null)
  const [sliderData, setSliderData] = useState({media: []} as { media: TypeCmsMedium[] })
  const refDescription = useRef<HTMLHeadingElement | null>(null)
  const refSpecifications = useRef<HTMLHeadingElement | null>(null)
  const refConfigurator = useRef<HTMLHeadingElement | null>(null)
  const refAccessories = useRef<HTMLHeadingElement | null>(null)
  const refDownloads = useRef<HTMLHeadingElement | null>(null)
  const refContents = useRef<HTMLHeadingElement | null>(null)
  const refProjects = useRef<HTMLHeadingElement | null>(null)
  const refRelateds = useRef<HTMLHeadingElement | null>(null)

  const AnchorsData = useMemo(() => data ? getPageAnchors(data, lang, {
    refDescription,
    refSpecifications,
    refConfigurator,
    refAccessories,
    refDownloads,
    refContents,
    refProjects,
    refRelateds
  }) : [], [data, lang])

  const defaultModelDownload = useMemo(() => !selectedModel ? null : models?.filter(item => item.slug + '' === selectedModel)?.[0]?.downloads?.filter(item => item?.spec_sheet)?.[0], [selectedModel, models])
  const sageId = useMemo(() => !selectedModel ? models?.[0]?.sage_id : models?.filter(item => item.slug + '' === selectedModel)?.[0]?.sage_id, [selectedModel, models])
	const selectedModelPlans = useMemo(() => !selectedModel ? models[0]?.plans : models?.filter(item => item.slug + '' === selectedModel)?.[0]?.plans ?? [], [selectedModel, models])

  useEffect(() => {
    setSelectedModel(`${models[0]?.slug}`)
    setColorsOfSelectedModel(models[0]?.colors ?? null)
    setColorSelected(models[0]?.colors?.[0]?.ids?.join(',') ?? null)
  }, [models])

  useEffect(() => {
    if(colorSelected) {
      const mediaColorOfSelectedModel = colorsOfSelectedModel.find((color) => color.ids.join(',') === colorSelected)?.medium
      setSliderData({ media: mediaColorOfSelectedModel ? [mediaColorOfSelectedModel] : [] })

    } else {
      setSliderData({ media: [] })
    }
  }, [colorSelected, colorsOfSelectedModel])

  useEffect(() => {
    if (!sageId) return
    let timeoutId: NodeJS.Timeout | null = null
    if(searchParamSpecSheet && !urlChecked) {
      urlChecked = true
      updatePayloadModalSpecPdf({
        payload: {
          name,
          sageId,
          code: searchParamSpecSheet,
          slug
        },
        open: true
      })

      timeoutId = setTimeout(() => {
        setCortinaTransitionName('done')
        setTransitionName(null)
        setIsLoading(false)
      }, 200)

      return () => clearTimeout(timeoutId)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sageId, searchParamSpecSheet, slug])

  return (
    <Main>
      <SectionAnchors data={AnchorsData} />
      <div className='s-wrapper'>
        <Breadcrumbs data={{ category, filter, name }} />
        <SectionDescription data={{ description, name }} ref={refDescription} />
        <DynamicImageSliderVertical data={{ media }} hasToOpen={false} className='s--vertical-desc'/>
        <DynamicProductSlider data={models} setSelectedModel={setSelectedModel} selectedModel={selectedModel} setColorsOfSelectedModel={setColorsOfSelectedModel}/>
      </div>
      <div className='s-wrapper' style={{position: 'relative'}}>
        <AnimatePresence mode='wait'>
          <motion.div className='media-wrapper' key={`${selectedModel?.split('_')[0]}-${colorSelected}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{opacity: 0}} transition={{ duration: 0.3 }}>
            <DynamicImageSliderVertical data={sliderData} className='s--vertical-conf' />
          </motion.div>
        </AnimatePresence>
        {specifications && <SectionSpecifications data={{ specs: specifications, colors: colorsOfSelectedModel, displayAvailableColorsTitle, blueprints: selectedModelPlans }} ref={refSpecifications} setColorSelected={setColorSelected} />}
        {configurator?.options?.length > 0 && <SectionConfigurator data={configurator} ref={refConfigurator} selectedModel={selectedModel} setSelectedModel={setSelectedModel} defaultModelDownload={defaultModelDownload} sageId={sageId} initialModelSlug={models[0]?.slug} />}
      </div>
      {accessories?.length > 0 && <SectionAccessories data={{ accessories, name }} ref={refAccessories} />}
      {models?.length > 0 && <SectionDownloadsPerModel data={models} ref={refDownloads} selectedModel={selectedModel}/>}
      {contents?.length > 0 && <SectionContents data={contents} ref={refContents} />}
      {projects?.length > 0 && <SectionExploreProjects data={projects} ref={refProjects} background={false}/>}
      <SectionFitsIntoYourProject />
      {related_products && <SectionRelateds data={related_products} ref={refRelateds} />}
      <DynamicModalZoom />
      <DynamicModalSpecPdf />
    </Main>
  )
})

const Main = styled.main`
  section {
    &:first-child {
      z-index: 101;
    }
  }

  .s-wrapper {
    ${designGrid({})}
      width: 100vw;
      width: 100dvw;

    ${mq.greaterThan('mobile')} {
      min-height: 100vh;
      min-height: calc(100vh - ${vw(NAVBAR_HEIGHTS.mobile, 'mobile')});
    }

    ${mq.greaterThan('nexus7')} {
      min-height: calc(100vh - ${vw(NAVBAR_HEIGHTS.mobile, 'tablet')});
    }

    ${mq.greaterThan('tablet')} {
      min-height: calc(80vh - ${vw(NAVBAR_HEIGHTS.desktop, 'desktop')});
    }

    ${mq.greaterThan('desktop')} {
      /* min-height: calc(80vh - ${NAVBAR_HEIGHTS.desktop}px); */
      min-height: unset;
    }

    section {
      &[aria-labelledby='description'] {
        padding-top: 70px;
      }
    }

    section[aria-labelledby='description'],
    div.s--vertical-desc {
      ${mq.greaterThan('tablet')} {
        padding-top: 90px;
      }
    }


    &:has(.product_slider) {
      grid-template-rows: auto;

      /* ${mq.greaterThan('tablet')} {
        grid-template-rows: auto auto 240px;
      } */
    }

    > .media-wrapper {
      grid-column: auto / span 6;
      grid-row: 2 / span 1;
      height: auto;
      margin-bottom: 5px;
      margin-top: 20px;
      /* max-height: 400px; */
      position: relative;
      width: 100%;

      ${mq.greaterThan('tablet')} {
        grid-column: 9 / span 4;
        grid-row: 1 / span 1;
        margin-top: 100px;
        /* position: sticky;
        top: 110px; */
      }

      > div {
        margin: 0;
        margin-bottom: 0 !important;
      }
      .placeholder {
        background-color: var(--color-img-placeholder);
        height: 100%;
        width: 100%;
      }
    }

    .s--vertical-desc {
      grid-row: 3 / span 1;

      ${mq.greaterThan('tablet')} {
        grid-row: 2 / span 2;
      }
    }
  }
`
